<template>
  <v-dialog
    :value="dialog"
    @input="v => !v && close()"
    scrollable
    max-width="720"
  >
    <v-card>
      <v-toolbar dark dense flat class="white--text">
        <v-toolbar-title class="font-weight-regular"
          >Coincidencias</v-toolbar-title
        >
        <v-spacer></v-spacer>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: 65vh">
        <div v-if="loading" class="pa-6 text-center">
          <v-progress-circular indeterminate />
        </div>
        <v-alert v-else-if="error" type="error" text class="ma-4">{{
          error
        }}</v-alert>
        <template v-else>
          <div
            v-if="canLink && hasCandidates"
            class="px-4 py-3 caption grey--text text--darken-1 match-help"
          >
            Selecciona los registros que corresponden a la misma persona que
            <strong>este lead</strong> para agruparlos en un solo contacto.
          </div>
          <div v-if="!matches.length" class="pa-6 text-center grey--text">
            Sin coincidencias.
          </div>

          <div v-for="group in groups" :key="group.key" class="match-group">
            <!-- encabezado del grupo -->
            <div
              class="match-group__header d-flex align-center px-4 py-2"
              :class="`match-group__header--${group.type}`"
            >
              <v-checkbox
                v-if="canLink && group.type === 'candidate-contact'"
                :input-value="groupSelected(group)"
                hide-details
                dense
                class="mt-0 pt-0 mr-2"
                @change="toggleGroup(group, $event)"
              />
              <v-icon
                small
                class="mr-2"
                :color="group.type === 'contact' ? 'primary' : 'grey'"
                >{{ group.icon }}</v-icon
              >
              <div>
                <div class="font-weight-bold body-2">{{ group.title }}</div>
                <div class="caption grey--text text--darken-1">
                  {{ group.subtitle }}
                </div>
              </div>
            </div>

            <!-- registros del grupo -->
            <div
              v-for="item in group.items"
              :key="item.lead_id"
              class="match-item px-4 py-3"
              :class="{ 'match-item--indent': group.type !== 'current' }"
            >
              <div class="d-flex align-center flex-wrap">
                <v-checkbox
                  v-if="canLink && group.type === 'loose'"
                  v-model="selected"
                  :value="item.lead_id"
                  hide-details
                  dense
                  class="mt-0 pt-0 mr-2"
                />
                <strong>{{ item.name }}</strong>
                <v-chip
                  v-if="item.is_original"
                  x-small
                  class="ml-2"
                  color="primary"
                  >Original</v-chip
                >
                <v-chip v-if="item.is_current" x-small class="ml-2" outlined
                  >Este lead</v-chip
                >
                <v-chip
                  v-if="!item.linked && !item.is_current && item.match"
                  x-small
                  class="ml-2"
                  :color="item.match.level === 'exact' ? 'orange' : 'grey'"
                  dark
                >
                  {{ item.match.level === "exact" ? "Exacta" : "Parcial" }}:
                  {{ matchFields(item.match.fields) }}
                </v-chip>
                <v-spacer />
                <span class="caption grey--text">{{
                  formatDate(item.created_at)
                }}</span>
                <v-btn
                  v-if="canUnlink && group.type === 'contact'"
                  x-small
                  text
                  color="error"
                  class="ml-2"
                  @click="askUnlink(item)"
                  >Desvincular</v-btn
                >
              </div>
              <div class="caption mt-1">
                <div>
                  Tel: {{ item.phone || "—" }} · Correo:
                  {{ item.email || "—" }}
                </div>
                <div v-if="item.campaign || item.ad_set">
                  <span v-if="item.campaign">Campaña: {{ item.campaign }}</span>
                  <span v-if="item.ad_set">
                    <template v-if="item.campaign"> · </template>Conjunto:
                    {{ item.ad_set }}
                  </span>
                </div>
                <div v-if="item.ad || item.form">
                  <span v-if="item.ad">Anuncio: {{ item.ad }}</span>
                  <span v-if="item.form">
                    <template v-if="item.ad"> · </template>Formulario:
                    {{ item.form }}
                  </span>
                </div>
                <div>
                  Fuente: {{ item.source || "—" }} · Medio:
                  {{ item.medium || "—" }}
                </div>
                <div v-if="zones(item)">Desarrollo: {{ zones(item) }}</div>
                <div>
                  Fase: {{ phaseText(item) }} · Asesor:
                  {{ brokerName(item.contact_broker_id) }}
                </div>
              </div>
            </div>
            <v-divider />
          </div>

          <v-alert
            v-if="canLink && summary"
            :type="hasConflict ? 'warning' : 'info'"
            text
            dense
            class="mx-4 mt-3 mb-0"
            >{{ summary }}</v-alert
          >
          <div v-if="canLink && needsBase" class="pa-4">
            <v-select
              v-model="baseLeadId"
              :items="baseOptions"
              item-text="label"
              item-value="lead_id"
              label="Registro base del nuevo contacto (el más completo)"
              hide-details
              outlined
              dense
            />
          </div>
          <v-alert v-if="linkError" type="error" text class="ma-4">{{
            linkError
          }}</v-alert>
        </template>
      </v-card-text>

      <v-card-actions v-if="canLink && !loading && !error">
        <v-spacer />
        <v-btn text @click="close">Cerrar</v-btn>
        <v-btn
          color="primary"
          :disabled="!canSubmit"
          :loading="linking"
          @click="link"
          >Vincular a contacto</v-btn
        >
      </v-card-actions>
    </v-card>
    <v-dialog v-model="unlinkDialog" max-width="420">
      <v-card v-if="unlinkItem">
        <v-card-title class="text-h6">Desvincular del contacto</v-card-title>
        <v-card-text>
          <strong>{{ unlinkItem.name }}</strong> dejará de pertenecer al
          contacto. El lead se conserva con todos sus datos, asesor, fase y
          comentarios; solo sale del expediente.
          <v-alert
            v-if="unlinkError"
            type="error"
            text
            dense
            class="mt-3 mb-0"
            >{{ unlinkError }}</v-alert
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="unlinking" @click="unlinkDialog = false"
            >Cancelar</v-btn
          >
          <v-btn color="error" :loading="unlinking" @click="unlink"
            >Desvincular</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import {
  GetLeadMatches,
  LinkLeadsToContact,
  UnlinkLeadFromContact
} from "@/api/lead-api.js";
import { GetContactBrokerList } from "@/api/contact-broker-api.js";
import { DEFAULT_PHASES } from "@/constants/phases";

const FIELD_LABELS = { name: "nombre", email: "correo", phone: "teléfono" };

export default {
  name: "LeadMatchesModal",
  props: {
    dialog: { type: Boolean, default: false },
    leadId: { type: String, default: "" }
  },
  data() {
    return {
      loading: false,
      linking: false,
      error: "",
      linkError: "",
      contact: null,
      matches: [],
      selected: [],
      baseLeadId: "",
      brokerList: [],
      unlinkDialog: false,
      unlinkItem: null,
      unlinking: false,
      unlinkError: ""
    };
  },
  computed: {
    canLink() {
      return ["root", "admin", "coordinator"].includes(
        localStorage.getItem("user_role")
      );
    },
    // desvincular: solo root y admin (el backend responde 403 al resto)
    canUnlink() {
      return ["root", "admin"].includes(localStorage.getItem("user_role"));
    },
    // sin contacto aún: hay que crearlo eligiendo el registro base
    // contactos distintos entre los candidatos marcados
    selectedContactIds() {
      return [
        ...new Set(
          this.matches
            .filter(m => this.selected.includes(m.lead_id))
            .map(m => m.unified_contact_id)
            .filter(Boolean)
        )
      ];
    },
    // el backend responde 409 si se mezclan contactos distintos
    hasConflict() {
      const ids = this.selectedContactIds;
      if (this.contact) return ids.some(id => id !== this.contact._id);
      return ids.length > 1;
    },
    // sin contacto propio, pero un candidato marcado ya tiene uno: se une a ese
    existingContact() {
      if (this.contact || this.selectedContactIds.length !== 1) return null;
      const id = this.selectedContactIds[0];
      const m = this.matches.find(x => x.unified_contact_id === id);
      return { _id: id, name: (m && m.contact_name) || "" };
    },
    needsBase() {
      return !this.contact && !this.existingContact && this.selected.length > 0;
    },
    // el registro base debe estar dentro de lead_ids
    baseOptions() {
      const ids = this.leadIdsToSend;
      return this.matches
        .filter(m => ids.includes(m.lead_id))
        .map(m => ({
          lead_id: m.lead_id,
          label: `${m.name} (${this.filledFields(m)} campos)${
            m.lead_id === this.suggestedBase ? " — sugerido" : ""
          }`
        }));
    },
    leadIdsToSend() {
      if (this.contact) return this.selected;
      // sin contacto, linked es false para todos: el lead actual se incluye siempre
      return [...new Set([this.leadId, ...this.selected])];
    },
    suggestedBase() {
      const ids = this.leadIdsToSend;
      const best = this.matches
        .filter(m => ids.includes(m.lead_id))
        .sort((a, b) => this.filledFields(b) - this.filledFields(a))[0];
      return best ? best.lead_id : "";
    },
    // secciones: expediente del contacto, este lead, candidatos por contacto y sueltos
    groups() {
      const plural = n => `${n} registro${n === 1 ? "" : "s"}`;
      const out = [];
      const linked = this.matches.filter(m => m.linked);
      if (this.contact && linked.length) {
        out.push({
          key: "contact",
          type: "contact",
          icon: "mdi-folder-account-outline",
          title: `Contacto: ${this.contact.name}`,
          subtitle: `Expediente del cliente · ${plural(
            linked.length
          )} agrupados`,
          items: linked
        });
      }
      const current = this.matches.filter(m => m.is_current && !m.linked);
      if (current.length) {
        out.push({
          key: "current",
          type: "current",
          icon: "mdi-account-outline",
          title: "Este lead",
          subtitle: "Todavía no pertenece a ningún contacto",
          items: current
        });
      }
      const candidates = this.matches.filter(m => !m.linked && !m.is_current);
      const byContact = {};
      candidates
        .filter(m => m.unified_contact_id)
        .forEach(m => {
          (byContact[m.unified_contact_id] =
            byContact[m.unified_contact_id] || []).push(m);
        });
      Object.keys(byContact).forEach(id => {
        const items = byContact[id];
        out.push({
          key: `candidate-${id}`,
          type: "candidate-contact",
          contactId: id,
          icon: "mdi-folder-account-outline",
          title: `Contacto: ${items[0].contact_name || ""}`,
          subtitle: `Ya agrupa ${plural(
            items.length
          )} que coinciden. Marca para unir este lead a ese contacto.`,
          items
        });
      });
      const loose = candidates.filter(m => !m.unified_contact_id);
      if (loose.length) {
        out.push({
          key: "loose",
          type: "loose",
          icon: "mdi-account-search-outline",
          title: "Posibles coincidencias sin contacto",
          subtitle: "Marca los que sean la misma persona.",
          items: loose
        });
      }
      return out;
    },
    hasCandidates() {
      return this.matches.some(m => !m.linked && !m.is_current);
    },
    // resultado de la acción antes de confirmar
    summary() {
      const n = this.selected.length;
      if (!n) return "";
      const registros = `${n} registro${n === 1 ? "" : "s"}`;
      if (this.hasConflict)
        return "Los registros marcados pertenecen a contactos distintos. No se pueden mezclar: desmarca los de uno de ellos.";
      if (this.existingContact)
        return `Este lead y ${registros} se unirán al contacto "${this.existingContact.name}".`;
      if (this.contact)
        return `Se agregará${n === 1 ? "" : "n"} ${registros} al contacto "${
          this.contact.name
        }".`;
      return `Este lead y ${registros} quedarán agrupados en un mismo contacto. Si alguno ya pertenece a un contacto, se unirán a ese.`;
    },
    canSubmit() {
      if (!this.selected.length || this.hasConflict) return false;
      if (this.contact || this.existingContact) return true;
      return this.leadIdsToSend.length >= 2 && !!this.baseLeadId;
    },
    brokers() {
      const fromStore = this.$store.state.leads?.filter?.filters?.contacts;
      return fromStore && fromStore.length ? fromStore : this.brokerList;
    },
    phases() {
      return this.$store.getters["leads/filter/getPhases"] || DEFAULT_PHASES;
    }
  },
  watch: {
    dialog(val) {
      if (val) this.load();
    },
    suggestedBase(val) {
      if (!this.baseLeadId || !this.leadIdsToSend.includes(this.baseLeadId))
        this.baseLeadId = val;
    }
  },
  mounted() {
    // al montarse ya abierto (desde la lista) no hay cambio que dispare el watch
    if (this.dialog) this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      this.linkError = "";
      this.selected = [];
      this.baseLeadId = "";
      try {
        this.loadBrokers();
        const res = await GetLeadMatches(this.leadId);
        this.contact = res.contact;
        this.matches = res.matches;
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    // si la lista de asesores no está en el store, se consulta una vez
    async loadBrokers() {
      if (this.brokers.length) return;
      try {
        const res = await GetContactBrokerList();
        this.brokerList = res.ContactBroker || [];
      } catch (e) {
        this.brokerList = [];
      }
    },
    phaseText(item) {
      const found = this.phases.find(
        p =>
          p.data &&
          p.data.phase === item.phase &&
          (p.data.tracking_phase || "") === (item.tracking_phase || "")
      );
      if (found) return found.text;
      const byPhase = this.phases.find(p => p.value === item.phase);
      return byPhase ? byPhase.text : item.phase || "—";
    },
    async link() {
      this.linking = true;
      this.linkError = "";
      try {
        const payload = { lead_ids: this.leadIdsToSend };
        const target = this.contact || this.existingContact;
        if (target) payload.contact_id = target._id;
        else payload.base_lead_id = this.baseLeadId;
        const res = await LinkLeadsToContact(payload);
        this.$snotify.success(`Leads vinculados: ${res.linked}`);
        this.$emit("linked");
        await this.load();
      } catch (e) {
        this.linkError = e.message;
      } finally {
        this.linking = false;
      }
    },
    askUnlink(item) {
      this.unlinkItem = item;
      this.unlinkError = "";
      this.unlinkDialog = true;
    },
    async unlink() {
      this.unlinking = true;
      this.unlinkError = "";
      try {
        const res = await UnlinkLeadFromContact(this.unlinkItem.lead_id);
        this.unlinkDialog = false;
        this.$snotify.success(
          res.contact_deleted
            ? "Lead desvinculado. El contacto quedó vacío y se eliminó."
            : "Lead desvinculado del contacto."
        );
        this.$emit("linked");
        await this.load();
      } catch (e) {
        this.unlinkError =
          e.status === 403 ? "No tienes permiso para desvincular" : e.message;
      } finally {
        this.unlinking = false;
      }
    },
    groupSelected(group) {
      return group.items.every(m => this.selected.includes(m.lead_id));
    },
    toggleGroup(group, checked) {
      const ids = group.items.map(m => m.lead_id);
      const rest = this.selected.filter(id => !ids.includes(id));
      this.selected = checked ? [...rest, ...ids] : rest;
    },
    close() {
      this.$emit("update:dialog", false);
    },
    filledFields(m) {
      return [m.name, m.phone, m.email, m.zones && m.zones.length].filter(
        Boolean
      ).length;
    },
    matchFields(fields = []) {
      return fields.map(f => FIELD_LABELS[f] || f).join(", ");
    },
    zones(item) {
      const z = item.zones;
      if (!z) return "";
      return Array.isArray(z)
        ? z.map(x => (x && x.name) || x).join(", ")
        : String(z);
    },
    brokerName(id) {
      const b = this.brokers.find(x => x._id === id);
      return b ? b.name : "Sin asignar";
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style scoped>
.match-group__header {
  background: rgba(0, 0, 0, 0.04);
}
.match-group__header--contact {
  background: rgba(25, 118, 210, 0.08);
  border-left: 3px solid #1976d2;
}
.match-item--indent {
  padding-left: 40px !important;
}
</style>
